'use client'

// import dependencies
import { useState, useEffect, useRef } from 'react'

// import components
import Link from 'next/link'
import Icon from '@mdi/react'

// import icons
import {
	mdiOpenInNew,
	mdiChevronLeft,
	mdiChevronRight,
	mdiFullscreen,
	mdiFullscreenExit,
	mdiCircle,
	mdiCircleOutline,
	mdiVolumeOff,
	mdiVolumeHigh,
} from '@mdi/js'

export type HeroMedia = {
	src: string
	mime: string
}

export type HeroProps<T> = {
	item: T
	slug: string
	url?: string
	media?: HeroMedia[]
}

export const Hero = <T extends { id: string, name: string, tags: string[], summary: string | null }>({ item, slug, url, media }: HeroProps<T>) => (
	<HeroBackground item={item} slug={slug} media={media}>
		<div className='flex items-start justify-between gap-2'>
			<h1 className='text-4xl font-semibold'>{item.name}</h1>
			{url && (
				<Link href={url} target='_blank' className='flex items-center gap-2 rounded bg-white p-2 text-black hover:bg-100'>
					<span className='hidden lg:block'>Open {item.name}</span>
					<Icon path={mdiOpenInNew} size={1} />
				</Link>
			)}
		</div>
		<div className='flex w-full flex-col gap-3 lg:w-3/4'>
			<div className='flex gap-2 text-black'>
				{item.tags.map((tag, index) => (
					<Link href={`/${slug}?tags=${tag}`} key={index} className='cursor-pointer rounded bg-100 p-2 text-xs font-light capitalize hover:bg-200'>{tag}</Link>
				))}
			</div>
			<span className='hidden font-medium text-white md:block lg:text-base'>{item.summary}</span>
		</div>
	</HeroBackground>
)

const HeroBackground = <T extends { id: string, name: string, tags: string[], summary: string | null }>({ item, slug, media, children }: HeroProps<T> & { children: React.ReactNode }) => {
	const container = useRef<HTMLDivElement>(null)
	const video = useRef<HTMLVideoElement>(null)
	const [index, setIndex] = useState(0)
	const [fullscreen, setFullscreen] = useState(false)
	const [mute, setMute] = useState(true)

	// handle event listeners
	useEffect(() => {
		if (!media || media.length === 0) return

		const onKeydown = (event: KeyboardEvent) => {
			if (event.key === 'ArrowLeft') {
				setIndex(x => (x - 1 + media.length) % media.length)
			} else if (event.key === 'ArrowRight') {
				setIndex(x => (x + 1) % media.length)
			}
		}

		const onFullscreenChange = () => {
			if (document.fullscreenElement) {
				setFullscreen(true)
			} else {
				setFullscreen(false)
			}
		}

		document.addEventListener('keydown', onKeydown)
		document.addEventListener('fullscreenchange', onFullscreenChange)

		return () => {
			document.removeEventListener('keydown', onKeydown)
			document.removeEventListener('fullscreenchange', onFullscreenChange)
		}
	}, [media])

	// handle video mute
	useEffect(() => {
		if (video.current) {
			video.current.muted = mute
		}
	}, [video, mute])

	// handle fullscreen
	useEffect(() => {
		if (container.current) {
			if (fullscreen) {
				container.current.requestFullscreen()
			} else {
				document.fullscreenElement && document.exitFullscreen()
			}
		}
	}, [container, fullscreen])

	if (!media || media.length < 2) return (
		<div className='flex h-[720px] shrink-0 flex-col justify-between gap-8 p-8 md:p-12' style={{ background: `linear-gradient(to right top, rgba(0, 0, 0, 0.50) 25%, rgba(0, 0, 0, 0.00) 100%), url("/api/images/${slug}/${item.id}/hero") lightgray 50% / cover no-repeat` }}>
			{children}
		</div>
	)

	return (
		<div ref={container} className='relative flex h-[720px] shrink-0'>
			<button onClick={() => setIndex(x => (x - 1 + media.length) % media.length)} className='absolute inset-y-8 left-0 z-20 flex w-8 cursor-pointer items-center justify-center hover:text-white/75 md:inset-y-12 md:w-12'>
				<Icon path={mdiChevronLeft} size={1} />
			</button>
			<button onClick={() => setIndex(x => (x + 1) % media.length)} className='absolute inset-y-8 right-0 z-20 flex w-8 cursor-pointer items-center justify-center hover:text-white/75 md:inset-y-12 md:w-12'>
				<Icon path={mdiChevronRight} size={1} />
			</button>
			<div className='absolute inset-x-8 bottom-0 z-20 flex h-8 items-center justify-center md:inset-x-12 md:h-12'>
				{media.map((_, i) => (
					<button key={i} onClick={() => setIndex(i)} className='flex size-4 cursor-pointer items-center justify-center hover:text-white/75'>
						{i === index ? <Icon path={mdiCircle} size={0.5} /> : <Icon path={mdiCircleOutline} size={0.5} />}
					</button>
				))}
			</div>
			{media[index].mime.startsWith('video') && (
				<button onClick={() => setMute(x => !x)} className='absolute bottom-0 left-0 z-20 flex size-8 cursor-pointer items-center justify-center hover:text-white/75 md:size-12'>
					{mute ? <Icon path={mdiVolumeOff} size={1} /> : <Icon path={mdiVolumeHigh} size={1} />}
				</button>
			)}
			<button onClick={() => setFullscreen(x => !x)} className='absolute bottom-0 right-0 z-20 flex size-8 cursor-pointer items-center justify-center hover:text-white/75 md:size-12'>
				{fullscreen ? <Icon path={mdiFullscreenExit} size={1} /> : <Icon path={mdiFullscreen} size={1} />}
			</button>
			<div className='absolute inset-0 z-0'>
				{!fullscreen && (
					<div className='absolute inset-0' style={{ background: 'linear-gradient(to right top, rgba(0, 0, 0, 0.50) 25%, rgba(0, 0, 0, 0.00) 100%)' }}></div>
				)}
				{media[index].mime.startsWith('video') ? (
					<video autoPlay loop muted={mute} ref={video} className='size-full object-cover'>
						<source src={media[index].src} type={media[index].mime} />
						Your browser does not support the video tag.
					</video>
				) : (
					<img src={media[index].src} alt={item.name} className='size-full object-cover' />
				)}
			</div>
			<div className='z-10 flex w-full flex-col justify-between gap-8 p-8 md:p-12'>
				{!fullscreen && children}
			</div>
		</div >
	)
}

export default Hero